import { backendProvider } from '../backend';
import { getCoreWsUrl } from './coreConfig';

// Backoff resets to 1s only once the server actually confirms `connected` —
// reaching socket `open` is not enough, since an expired JWT opens fine and
// then gets rejected by the `/auth` handshake.
//
// Espen's rule: every retry loop must be exponential-backoff WITH jitter AND
// have a retry limit. An always-open tab whose session has died otherwise
// mints a doomed JWT every 30s forever (~2,900 `/account/jwts` 401s/day/tab —
// the source of the 355k errors seen server-side).
const INITIAL_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;
/** Give up after this many consecutive failures; a later connect() restarts. */
const MAX_CONSECUTIVE_FAILURES = 10;

type MessageType = 'connected' | 'notification' | 'auth_error';
type Handler = (data: any) => void;

const subscribers = new Map<MessageType, Set<Handler>>();

let socket: WebSocket | null = null;
let retryDelayMs = INITIAL_RETRY_DELAY_MS;
let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
let shouldStayConnected = false;
let consecutiveFailures = 0;

const emit = (type: MessageType, data: any) => {
  subscribers.get(type)?.forEach((handler) => handler(data));
};

const scheduleReconnect = () => {
  if (!shouldStayConnected) return;

  consecutiveFailures += 1;
  if (consecutiveFailures > MAX_CONSECUTIVE_FAILURES) {
    // Circuit breaker: stop hammering /account/jwts. The next explicit
    // connect() (remount, login, user action) resets and restarts the loop.
    console.warn(
      `🛑 Notification socket: giving up after ${consecutiveFailures} consecutive failed attempts — will retry on next connect()`,
    );
    shouldStayConnected = false;
    retryDelayMs = INITIAL_RETRY_DELAY_MS;
    consecutiveFailures = 0;
    return;
  }

  // Exponential backoff with ±30% jitter so many tabs don't resynchronize
  // into a thundering herd after a shared server blip.
  const jitter = retryDelayMs * (0.7 + Math.random() * 0.6);
  retryTimeoutId = setTimeout(() => {
    void openSocket();
  }, jitter);
  retryDelayMs = Math.min(retryDelayMs * 2, MAX_RETRY_DELAY_MS);
};

const openSocket = async () => {
  if (!shouldStayConnected) return;

  // Mint a fresh JWT on every connect attempt — never reuse one across attempts.
  const jwt = await backendProvider.auth.getIdToken(true);
  if (!shouldStayConnected) return;

  if (!jwt) {
    // JWT mint failed → there is no valid session right now. Opening the
    // socket would be guaranteed-rejected; back off instead of connecting.
    scheduleReconnect();
    return;
  }

  const ws = new WebSocket(getCoreWsUrl());
  socket = ws;

  ws.addEventListener('open', () => {
    ws.send(JSON.stringify({ endpoint: '/auth', jwt }));
  });

  ws.addEventListener('message', (event) => {
    let parsed: { type?: MessageType; data?: any };
    try {
      parsed = JSON.parse(event.data);
    } catch {
      return;
    }
    if (!parsed.type) return;

    if (parsed.type === 'connected') {
      // Server genuinely accepted us — only now reset the backoff chain.
      retryDelayMs = INITIAL_RETRY_DELAY_MS;
      consecutiveFailures = 0;
    }
    emit(parsed.type, parsed.data);
  });

  const handleDisconnect = () => {
    if (socket === ws) {
      socket = null;
    }
    scheduleReconnect();
  };

  ws.addEventListener('close', handleDisconnect);
  ws.addEventListener('error', handleDisconnect);
};

export const coreSocket = {
  connect(): void {
    if (shouldStayConnected) return;
    shouldStayConnected = true;
    retryDelayMs = INITIAL_RETRY_DELAY_MS;
    consecutiveFailures = 0;
    void openSocket();
  },

  disconnect(): void {
    shouldStayConnected = false;
    if (retryTimeoutId) {
      clearTimeout(retryTimeoutId);
      retryTimeoutId = undefined;
    }
    socket?.close();
    socket = null;
  },

  subscribe(type: MessageType, handler: Handler): () => void {
    if (!subscribers.has(type)) {
      subscribers.set(type, new Set());
    }
    subscribers.get(type)!.add(handler);
    return () => {
      subscribers.get(type)?.delete(handler);
    };
  },
};
